/**
 * Adapter: Alurack Redux segments → pv-layout-core initialProject format.
 *
 * Consumes the segments array from Redux (already normalized by
 * `common/components/segment/helpers.js::formatSegment`) along with the
 * parent field segment location and outputs the shape expected by the
 * `SolarPlanner` widget as `initialProject`.
 *
 * Alurack-specific notes:
 *  - Alurack has no permissions/v5 concept, so this file avoids the
 *    "v5" terminology used by the Sunwise adapter.
 *  - Extra Alurack custom_inputs (type_application, system_uid, sloof_type,
 *    gclamp_type, category, preset_configuration, land_category and the
 *    solution_fields: rail_spacing, rail_support_length, plate_support_length,
 *    beam_spacing, support_length, lfoot_supports_width, outer_rail_length,
 *    front_base_height, seam_distance, sheet_gauge) are forwarded inside
 *    `settings` so the library can echo them back on create/update.
 */

import {
    POLYGON_CIRCLE,
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
} from 'common/components/segment/constants';

const ALURACK_EXTRA_CUSTOM_INPUTS = [
    'type_application',
    'system_uid',
    'system',
    'system_name',
    'land_category',
    'sloof_type',
    'gclamp_type',
    'category',
    'preset_configuration',
    'rail_spacing',
    'rail_support_length',
    'plate_support_length',
    'beam_spacing',
    'support_length',
    'lfoot_supports_width',
    'outer_rail_length',
    'front_base_height',
    'seam_distance',
    'sheet_gauge',
    'ceil_tilt',
    'fill_algorithm',
    'drawing_type',
    // building_heading: the polygon long-axis direction saved by legacy for
    // rectangle roofs. Re-loaded here so _syncRectangleHeading can skip the
    // write when the stored value already matches.
    'building_heading',
    // Panel-tab metadata kept by AlurackPanelTabExtra so the brand selector
    // and custom panel inputs hydrate correctly when reopening a project.
    'brandName',
    'is_custom_module',
    'panel_name',
    'panel_weight',
];

/**
 * Top-level keys on the formatted Alurack segment that also need to flow
 * through to the library so the edit form can show human-readable labels
 * (not just opaque uids). These are output by `formatSegment`.
 */
const ALURACK_TOP_LEVEL_FORWARDS = [
    'system',
    'system_uid',
    'system_name',
    'type_application',
    // Per-segment validation banners produced by the Alurack backend; the
    // 3D library renders them in SegmentSummary via SegmentAlerts. Hosts
    // that don't run validation (e.g. Sunwise) simply omit the field.
    'segment_errors',
];

function _toNumberOrNull(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function _setNumericIfValid(target, key, value) {
    const parsed = _toNumberOrNull(value);
    if (parsed != null) target[key] = parsed;
}

function _mapLayoutAlgorithm(value) {
    if (value === 'dynamic') return 'adaptive';
    if (value === 'grid') return 'grid';
    return value;
}

function _mapFillAlgorithm(value) {
    if (value === 'u' || value === 'units') return 'units';
    if (value === 'kw') return 'kw';
    return 'fill';
}

/**
 * Detect whether an Alurack segment represents a rectangle roof.
 * Checks both seg.type (POLYGON_RECTANGLE=3) and
 * custom_inputs.drawing_type (used when backend stores type=0).
 */
function _isRectangleRoof(seg) {
    if (seg.type === POLYGON_RECTANGLE) return true;
    const ci = seg.custom_inputs ?? {};
    const dt = ci.drawingType ?? ci.drawing_type;
    return dt === POLYGON_RECTANGLE;
}

function _toEnabledFlag(value) {
    if (value === false || value === 0 || value === '0' || value === 'false') {
        return false;
    }
    if (value === true || value === 1 || value === '1' || value === 'true') {
        return true;
    }
    return true;
}

function _resolveCellEnabled(cell) {
    if (!cell || typeof cell !== 'object') return true;
    if ('enable' in cell) return _toEnabledFlag(cell.enable);
    if ('is_enable' in cell) return _toEnabledFlag(cell.is_enable);
    if ('enabled' in cell) return _toEnabledFlag(cell.enabled);
    if ('isEnabled' in cell) return _toEnabledFlag(cell.isEnabled);
    return true;
}

function _isCellLike(entry) {
    return (
        entry &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        (Array.isArray(entry.paths) || Array.isArray(entry.path))
    );
}

function _collectCellLike(node, out) {
    if (_isCellLike(node)) {
        out.push(node);
        return;
    }

    if (!Array.isArray(node)) return;
    for (const child of node) {
        _collectCellLike(child, out);
    }
}

function _normalizeGroupRows(groupRows) {
    if (!Array.isArray(groupRows) || groupRows.length === 0) return [];

    const flattened = [];
    _collectCellLike(groupRows, flattened);
    if (flattened.length === 0) return [];

    const byRow = new Map();
    for (const cell of flattened) {
        const rowNumber = Number.isFinite(Number(cell.row))
            ? Number(cell.row)
            : 0;
        if (!byRow.has(rowNumber)) {
            byRow.set(rowNumber, []);
        }
        byRow.get(rowNumber).push(cell);
    }

    return [...byRow.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, rowCells]) =>
            [...rowCells].sort((a, b) => {
                const aCol = Number.isFinite(Number(a.col)) ? Number(a.col) : 0;
                const bCol = Number.isFinite(Number(b.col)) ? Number(b.col) : 0;
                if (aCol !== bCol) return aCol - bCol;
                const aCell = Number.isFinite(Number(a.cell))
                    ? Number(a.cell)
                    : 0;
                const bCell = Number.isFinite(Number(b.cell))
                    ? Number(b.cell)
                    : 0;
                return aCell - bCell;
            }),
        );
}

/**
 * Build savedGroups from Alurack grouped_cells format, preserving the exact
 * panel geometry so the widget recreates the same layout on mount.
 *
 * grouped_cells: [groupIdx][rowIdx][panelsInRow] with enable/paths/row/col
 * savedGroups:   [{center, panels: [{center, corners, isEnabled}]}]
 */
function _buildSavedGroupsFromGroupedCells(groupedCells) {
    if (!Array.isArray(groupedCells) || groupedCells.length === 0) return [];

    return groupedCells
        .map((rawGroupRows) => {
            const groupRows = _normalizeGroupRows(rawGroupRows);
            if (groupRows.length === 0) {
                return null;
            }

            const allPanels = [];
            let groupCenterLat = 0;
            let groupCenterLng = 0;
            let panelCount = 0;

            for (const row of groupRows) {
                if (!Array.isArray(row)) continue;
                for (const cell of row) {
                    const cellPath = Array.isArray(cell.paths)
                        ? cell.paths
                        : Array.isArray(cell.path)
                          ? cell.path
                          : null;
                    if (
                        !cell ||
                        !Array.isArray(cellPath) ||
                        cellPath.length < 4
                    ) {
                        continue;
                    }
                    const corners = cellPath.map((p) => ({
                        lat: p[0],
                        lng: p[1],
                    }));
                    const centerLat =
                        corners.reduce((sum, c) => sum + c.lat, 0) /
                        corners.length;
                    const centerLng =
                        corners.reduce((sum, c) => sum + c.lng, 0) /
                        corners.length;

                    allPanels.push({
                        center: { lat: centerLat, lng: centerLng },
                        corners,
                        isEnabled: _resolveCellEnabled(cell),
                    });
                    groupCenterLat += centerLat;
                    groupCenterLng += centerLng;
                    panelCount++;
                }
            }

            if (panelCount === 0) return null;

            groupCenterLat /= panelCount;
            groupCenterLng /= panelCount;

            return {
                center: { lat: groupCenterLat, lng: groupCenterLng },
                panels: allPanels,
            };
        })
        .filter((g) => g != null);
}

/**
 * Convert Alurack Redux segments to the pv-layout-core initialProject format.
 *
 * @param {object} params
 * @param {Array} params.segments - Alurack formatted segments from Redux
 * @param {object} params.location - { latitude, longitude } from parentFieldSegment
 * @param {Array} params.offerPanels - Panel catalog from Redux
 * @returns {object} Library project ({ version, mapCenter, segments })
 */
export function alurackToLibrary({
    segments,
    location,
    offerPanels = [],
    proposalPanel = null,
}) {
    const librarySegments = [];

    for (const seg of segments) {
        const type = seg.type;

        if (type === POLYGON_SEGMENT || type === POLYGON_RECTANGLE) {
            librarySegments.push(_convertRoof(seg, offerPanels, proposalPanel));
        } else if (type === POLYGON_OBSTACLE || type === POLYGON_CIRCLE) {
            librarySegments.push(_convertObstacle(seg));
        } else if (type === POLYGON_MAP) {
            librarySegments.push(_convertImage(seg));
        }
    }

    return {
        version: 5,
        mapCenter: location
            ? { lat: location.latitude, lng: location.longitude }
            : null,
        segments: librarySegments,
    };
}

function _convertRoof(seg, offerPanels, proposalPanel) {
    const ring = _toRing(seg.polygon);
    const settings = {};
    const ci = seg.custom_inputs ?? {};

    if (seg.name) settings.name = seg.name;
    if (seg.orientation === 'P') settings.panelOrientation = 'vertical';
    else if (seg.orientation === 'L') settings.panelOrientation = 'horizontal';
    else if (ci.panel_orientation)
        settings.panelOrientation = ci.panel_orientation;

    if (seg.col_spacing != null) settings.panelGapX = seg.col_spacing;
    if (seg.row_spacing != null) settings.panelGapY = seg.row_spacing;
    if (seg.azimuth != null) settings.azimuthDeg = seg.azimuth;
    else _setNumericIfValid(settings, 'azimuthDeg', ci.azimuth_deg);
    if (seg.tilt != null) settings.tiltDeg = seg.tilt;
    else _setNumericIfValid(settings, 'tiltDeg', ci.tilt_deg);
    if (seg.frame_cols_number != null)
        settings.groupCols = seg.frame_cols_number;
    if (seg.frame_rows_number != null)
        settings.groupRows = seg.frame_rows_number;
    if (seg.frame_col_spacing != null)
        settings.groupGapX = seg.frame_col_spacing;
    if (seg.frame_row_spacing != null)
        settings.groupGapY = seg.frame_row_spacing;
    // Flat-roof systems (LASTRADA, GRANJA LOSA, LOSA CONVENCIONAL) store the
    // PANEL inclination in ceil_tilt, not the roof slope. roofSlopeDeg must
    // be 0 for these systems — mapping ceil_tilt to it would tilt the roof
    // surface and cause a staircase effect in the 3D view.
    // For all other systems, use seg.roof_tilt or ci.ceil_tilt as the slope.
    const _flatRoofUids = [
        'ARCK-GRANJA LOSA',
        'ARCK-LASTRADA DOBLE',
        'ARCK-LASTRADA SENCILLA',
        'ARCK-LOSA CONVENCIONAL',
    ];
    const _isFlatRoof =
        _flatRoofUids.includes(ci.system_uid) ||
        _flatRoofUids.includes(`ARCK-${ci.system_uid}`);
    if (_isFlatRoof) {
        settings.roofSlopeDeg = 0;
    } else if (seg.roof_tilt != null) {
        settings.roofSlopeDeg = seg.roof_tilt;
    } else {
        _setNumericIfValid(settings, 'roofSlopeDeg', ci.ceil_tilt);
    }
    if (seg.safe_zone != null) settings.setbackM = seg.safe_zone;
    // The user's editable building height lives ONLY in
    // `custom_inputs.building_height`. Top-level `seg.height` is a
    // backend-owned constant (commonly 1.5) that we must not interpret as
    // the building height — doing so would freeze the planner on 1.5
    // forever, ignoring whatever the user actually saved.
    _setNumericIfValid(settings, 'buildingHeight', ci.building_height);
    if (settings.buildingHeight == null && seg.height > 0) {
        settings.buildingHeight = seg.height;
    }

    // Algorithm mapping
    if (ci.algorithm) {
        const algoMap = { dynamic: 'adaptive', grid: 'grid' };
        settings.layoutAlgorithm = algoMap[ci.algorithm] ?? ci.algorithm;
    }
    if (ci.fill_algorithm) {
        settings.panelFillMode = _mapFillAlgorithm(ci.fill_algorithm);
    }
    if (ci.panel_fill_mode) {
        settings.panelFillMode = _mapFillAlgorithm(ci.panel_fill_mode);
    }
    if (ci.layout_algorithm) {
        settings.layoutAlgorithm = _mapLayoutAlgorithm(ci.layout_algorithm);
    }

    _setNumericIfValid(settings, 'panelWidth', ci.panel_width);
    _setNumericIfValid(settings, 'panelHeight', ci.panel_height);
    _setNumericIfValid(settings, 'panelDepth', ci.panel_depth);
    _setNumericIfValid(settings, 'panelWatts', ci.panel_watts);
    _setNumericIfValid(settings, 'panelLiftHeight', ci.panel_lift_height);
    _setNumericIfValid(
        settings,
        'panelSunGlareIntensity',
        ci.panel_sun_glare_intensity,
    );
    _setNumericIfValid(settings, 'targetPanelCount', ci.target_panel_count);
    _setNumericIfValid(settings, 'targetKw', ci.target_kw);
    _setNumericIfValid(settings, 'groupRows', ci.group_rows);
    _setNumericIfValid(settings, 'groupCols', ci.group_cols);
    _setNumericIfValid(settings, 'panelGapX', ci.panel_gap_x);
    _setNumericIfValid(settings, 'panelGapY', ci.panel_gap_y);
    _setNumericIfValid(settings, 'groupGapX', ci.group_gap_x);
    _setNumericIfValid(settings, 'groupGapY', ci.group_gap_y);
    _setNumericIfValid(settings, 'roofSlopeDeg', ci.roof_slope_deg);
    _setNumericIfValid(
        settings,
        'roofSlopeAzimuthDeg',
        ci.roof_slope_azimuth_deg,
    );
    _setNumericIfValid(settings, 'setbackM', ci.setback_m);
    _setNumericIfValid(settings, 'elevation', ci.elevation);
    // Ridge divider position for double_slope rectangular roofs.
    // Only applied when the stored value is a valid fraction in (0, 1).
    if (
        ci.ridge_offset_fraction != null &&
        Number.isFinite(Number(ci.ridge_offset_fraction)) &&
        Number(ci.ridge_offset_fraction) > 0 &&
        Number(ci.ridge_offset_fraction) < 1
    ) {
        settings.ridgeOffsetFraction = Number(ci.ridge_offset_fraction);
    }

    // Forward Alurack extras verbatim so the library hands them back on save
    for (const key of ALURACK_EXTRA_CUSTOM_INPUTS) {
        if (ci[key] !== undefined && ci[key] !== null) {
            settings[key] = ci[key];
        }
    }

    // Recover brandName and custom panel metadata seeded by `formatSegment` if they
    // are missing from custom_inputs (e.g. on first load of a legacy segment)
    if (settings.brandName == null && seg.brandName) {
        settings.brandName = seg.brandName;
    }
    if (settings.is_custom_module == null && seg.is_custom_module != null) {
        settings.is_custom_module = seg.is_custom_module;
    }
    if (settings.panel_name == null && seg.panel?.name) {
        settings.panel_name = seg.panel.name;
    }
    if (settings.panel_weight == null && seg.panel?.weight) {
        settings.panel_weight = seg.panel.weight;
    }

    // Forward human-readable top-level fields emitted by formatSegment so the
    // library's edit form can render proper labels (system_name, roof type
    // label) without needing Redux lookups on the widget side.
    for (const key of ALURACK_TOP_LEVEL_FORWARDS) {
        if (seg[key] !== undefined && seg[key] !== null && seg[key] !== '') {
            // Don't override an already-set custom_inputs value; custom_inputs
            // is what the user edited last.
            if (settings[key] == null) settings[key] = seg[key];
        }
    }

    // Derive `panelPairingMode` from the selected system so the library can
    // adjust its double-slope layout for ballasted double-incline systems
    // (ARCK-LASTRADA DOBLE) — panels meet at the ridge instead of having a
    // 1 m clearance on each side. Loaded segments pick this up here; newly
    // drawn segments get it from AlurackSolarPlanner's initial-settings path.
    const normalizedSystemUid = String(settings.system_uid || '')
        .replace(/^ARCK-/, '')
        .trim()
        .toUpperCase();
    if (normalizedSystemUid === 'LASTRADA DOBLE') {
        settings.panelPairingMode = 'back_to_back';
    } else if (normalizedSystemUid === 'LASTRADA SENCILLA') {
        // single_incline: all panels tilt the same direction. Each panel
        // rotates around its own center so rows sit flush on the roof surface.
        settings.panelPairingMode = 'single_incline';
    }

    // For flat-roof systems, sloof_type encodes the panel arrangement
    // (single vs double inclination) in Alurack's domain, but the 3D library
    // interprets it as a physical roof slope — passing it would tilt the roof
    // surface instead of the panels.  Clear it so the library renders a flat
    // roof with tilted panels (tiltDeg handles inclination).
    if (_isFlatRoof && settings.sloof_type) {
        delete settings.sloof_type;
    }

    // Alurack's `solution_fields` (beam_spacing, rail_spacing, ...) can live at
    // ci root (after merge) or under ci.solution_fields on older payloads.
    if (ci.solution_fields && typeof ci.solution_fields === 'object') {
        for (const [key, value] of Object.entries(ci.solution_fields)) {
            if (value !== undefined && value !== null && settings[key] == null)
                settings[key] = value;
        }
    }

    // Panel model resolution.
    // offerPanels items use `panel_company_branch_office` as their identifier
    // (mapped from `p.id` in AlurackSolarPlanner). Match on that field so
    // stock panels are found correctly.
    if (seg.solar_module) {
        const matched = offerPanels.find(
            (p) =>
                p.panel_company_branch_office === seg.solar_module ||
                p.id === seg.solar_module,
        );
        if (matched) {
            // Stock panel — seed selectedPanelCode and dimensions from the offer.
            settings.selectedPanelCode = seg.solar_module;
            settings.panelWidth = matched.width / 1000;
            settings.panelHeight = matched.height / 1000;
            settings.panelDepth = matched.depth / 1000;
            settings.panelWatts = matched.watts;
        }
        // Custom panels are NOT in offerPanels. Do NOT copy their UUID into
        // selectedPanelCode: libraryToAlurack would later forward it as
        // solar_module in the PUT payload, causing an IntegrityError if that
        // custom panel record was deleted from panels_panelcompany (e.g. after
        // a staging DB reset). Dimensions for custom panels come from seg.panel.
    } else if (ci.selected_panel_code) {
        settings.selectedPanelCode = ci.selected_panel_code;
    }

    // Seed brand + model name + weight for the Module tab (Alurack extras)
    // from the segment's `panel` object when custom_inputs didn't already
    // carry them. Legacy segments pre-date these custom_inputs keys, so the
    // panel object is the only place these values exist.
    const apiPanel = seg.panel ?? null;
    if (apiPanel) {
        // FIX: Load panel DIMENSIONS from seg.panel when they are still missing.
        //
        // panelWidth / panelHeight are required by the library's panelLayout()
        // function. If they are undefined, Math.max(0.1, undefined) = NaN and
        // the loop condition `cursor <= max - NaN` is always false → no panels
        // are placed → panels_layout = [] → field_segments.solar_modules = []
        // → backend wipes panel_modules → template shows empty tables.
        //
        // This situation occurs for two cases:
        //  1. Stock panels whose UUID is NOT in the current offerPanels (e.g.
        //     the proposal's selected panel changed after the segment was saved,
        //     or different-brand panels are used per segment).
        //  2. Custom panels (is_custom_module=true) — they are intentionally
        //     excluded from the offerPanels match above, and the comment that
        //     "dimensions come from seg.panel" was true as intent but the code
        //     was never written.
        //
        // seg.panel.width/height/depth are in mm (API units).
        // settings.panelWidth/Height/Depth are in meters (library units).
        if (settings.panelWidth == null && apiPanel.width != null) {
            settings.panelWidth = Number(apiPanel.width) / 1000;
        }
        if (settings.panelHeight == null && apiPanel.height != null) {
            settings.panelHeight = Number(apiPanel.height) / 1000;
        }
        if (settings.panelDepth == null && apiPanel.depth != null) {
            settings.panelDepth = Number(apiPanel.depth) / 1000;
        }
        if (settings.panelWatts == null) {
            const watts = apiPanel.max_power ?? apiPanel.watts;
            if (watts != null) settings.panelWatts = Number(watts);
        }

        if (settings.brandName == null) {
            const brandFromPanel =
                apiPanel.brand?.name ??
                apiPanel.brandName ??
                (apiPanel.is_custom ? 'custom' : null);
            if (brandFromPanel) settings.brandName = brandFromPanel;
        }
        if (settings.panel_name == null && apiPanel.name) {
            settings.panel_name = apiPanel.name;
        }
        if (settings.panel_weight == null && apiPanel.weight != null) {
            settings.panel_weight = Number(apiPanel.weight);
        }
        if (settings.is_custom_module == null) {
            settings.is_custom_module = apiPanel.is_custom === true;
        }
    }

    // FINAL FALLBACK: if panelWidth/Height are still missing (apiPanel was
    // null AND offerPanels had no match), use the proposal's default panel.
    // This is the most reliable source: it is always set on the proposal and
    // always has valid dimensions. Without valid panelWidth the library's
    // panelLayout() receives NaN → places zero panels → panels_layout = []
    // → field_segments.solar_modules = [] → backend creates no panel_modules
    // → BOM template tables are empty.
    if (!(settings.panelWidth > 0) && proposalPanel) {
        if (proposalPanel.width != null)
            settings.panelWidth = Number(proposalPanel.width) / 1000;
        if (proposalPanel.height != null)
            settings.panelHeight = Number(proposalPanel.height) / 1000;
        if (proposalPanel.depth != null && settings.panelDepth == null)
            settings.panelDepth = Number(proposalPanel.depth) / 1000;
        if (settings.panelWatts == null) {
            const w = proposalPanel.max_power ?? proposalPanel.watts;
            if (w != null) settings.panelWatts = Number(w);
        }
    }

    if (seg.quantity_enabled_modules != null) {
        settings.targetPanelCount = seg.quantity_enabled_modules;
    } else if (seg.solar_modules_number != null) {
        settings.targetPanelCount = seg.solar_modules_number;
    }

    // Build disabled panel keys from grouped_cells or solar_modules
    const disabledPanelKeys = [];
    const groupedCells = seg.grouped_cells ?? seg.modules_groups ?? [];

    if (groupedCells.length > 0) {
        for (
            let groupIndex = 0;
            groupIndex < groupedCells.length;
            groupIndex++
        ) {
            const groupRows = _normalizeGroupRows(groupedCells[groupIndex]);
            if (groupRows.length === 0) continue;

            let panelIndex = 0;
            for (const row of groupRows) {
                if (!Array.isArray(row)) continue;
                for (const cell of row) {
                    if (cell && !_resolveCellEnabled(cell)) {
                        disabledPanelKeys.push(`${groupIndex}:${panelIndex}`);
                    }
                    panelIndex++;
                }
            }
        }
    } else {
        const modulesByGroup = new Map();
        for (const pm of seg.solar_modules ?? []) {
            const gid = pm.group ?? 0;
            if (!modulesByGroup.has(gid)) {
                modulesByGroup.set(gid, []);
            }
            modulesByGroup.get(gid).push(pm);
        }

        const sortedGroups = [...modulesByGroup.entries()].sort(
            (a, b) => a[0] - b[0],
        );

        for (
            let groupIndex = 0;
            groupIndex < sortedGroups.length;
            groupIndex++
        ) {
            const [, modules] = sortedGroups[groupIndex];
            for (
                let panelIndex = 0;
                panelIndex < modules.length;
                panelIndex++
            ) {
                const pm = modules[panelIndex];
                if (pm && !_resolveCellEnabled(pm)) {
                    disabledPanelKeys.push(`${groupIndex}:${panelIndex}`);
                }
            }
        }
    }

    // Rectangle roofs must NOT use savedGroups on load. The ReadOnly viewer
    // reads panel positions from `solar_modules` (a separate backend field)
    // rather than `grouped_cells`, so it may be out-of-sync with the correct
    // grouped_cells even when those are stored correctly. Using stale
    // solar_modules positions via canUseSaved=true causes panels to render
    // at the wrong azimuth. Forcing canUseSaved=false makes useDerivedRoofData
    // recompute the layout from azimuthDeg, which is always stored correctly.
    // Compute the isRectangle flag once and reuse it to avoid calling
    // _isRectangleRoof(seg) twice and to short-circuit the savedGroups walk.
    const isRectangleRoof = _isRectangleRoof(seg);
    const savedGroups = isRectangleRoof
        ? []
        : _buildSavedGroupsFromGroupedCells(
              seg.grouped_cells ?? seg.modules_groups ?? [],
          );

    return {
        id: seg.id,
        type: 'roof',
        ring,
        ...(isRectangleRoof ? { isRectangle: true } : {}),
        settings,
        disabledPanelKeys,
        savedGroups,
    };
}

function _convertObstacle(seg) {
    const ring = _toRing(seg.polygon);
    const ci = seg.custom_inputs ?? {};
    const drawingType =
        ci.drawingType ?? ci.drawing_type ?? seg.drawingType ?? 0;
    const isCircle = drawingType === POLYGON_CIRCLE;
    const result = {
        id: seg.id,
        type: 'obstacle',
        height: seg.height ?? _toNumberOrNull(ci.height) ?? 0,
        elevation: _toNumberOrNull(ci.elevation) ?? 0,
        setbackM:
            _toNumberOrNull(ci.setback_m) ??
            _toNumberOrNull(seg.safe_zone) ??
            0,
        slopeDeg: _toNumberOrNull(ci.slope_deg) ?? 0,
        slopeAzimuthDeg: _toNumberOrNull(ci.slope_azimuth_deg) ?? 180,
        ring,
        isCircle,
    };
    if (seg.name) result.name = seg.name;
    if (isCircle && ci.diameter != null) result.diameter = ci.diameter;
    return result;
}

function _convertImage(seg) {
    const ring = _toRing(seg.polygon);
    return {
        id: seg.id,
        type: 'image',
        ring,
        imageUrl: seg.image ?? '',
        rotation: seg.rotate ?? 0,
        opacity: (seg.opacity ?? 100) / 100, // Alurack uses 0-100, library uses 0-1
        height: seg.height ?? 0,
        projectOnRoofs: seg.project_on_roofs ?? true,
    };
}

/**
 * Convert Alurack polygon [[lat, lng], ...] to library ring [{lat, lng}, ...]
 */
function _toRing(polygon) {
    if (!Array.isArray(polygon)) return [];
    return polygon.map((p) => {
        if (Array.isArray(p)) return { lat: p[0], lng: p[1] };
        return { lat: p.lat ?? p.y, lng: p.lng ?? p.x };
    });
}
